<template>
  <div v-if="selectedOption?.[0]?.value !== 'all'" class="sensorsMain">
    <div
      v-for="el in selectedSensorsData"
      :key="el.id"
      class="sensorsMain-container"
    >
      <div class="sensorsMain-container-general">
        <div class="sensorsMain-sensor">
          <div
            v-for="(item, index) in infoAboutSersor"
            :key="index"
            class="sensorsMain-sensor-info"
          >
            <p v-if="el[item.value]" class="title">{{ item.label }}</p>
            <span v-if="el[item.value]" class="value">
              {{ el[item.value] }}
            </span>
          </div>
        </div>
        <div
          v-if="chartDataById(el.sn)?.labels?.length && isDateCorrect"
          class="sensorsMain-diagram"
        >
          <LineChart
            :id="'lineChart_' + el.sn"
            :key="lineChartKey"
            :data-line-chart="chartDataById(el.sn)"
            :options-line-chart="optionsLineChart"
            :style="styleLineChart"
          />
        </div>
        <div class="sensorsMain-sensorInfo">
          <div v-if="temperatureSensorStamp?.timestamp" class="checkedSensor">
            <Checkbox name="checkedSensor" :value="checkedSensor" />
            <div class="container">
              <p class="value">
                {{ temperatureSensorStamp.userName }}
              </p>
              <p class="title">
                {{ dateTime(temperatureSensorStamp.timestamp) }}
              </p>
            </div>
          </div>
          <Button
            class="editSensor-button"
            variant="filled"
            :title="$t('editSensor')"
            @click="initEditSensor(el.id)"
          />
          <Button
            class="comment-button"
            variant="filled"
            :title="$t('comment')"
            @click="initComment(el.id)"
          />
        </div>
      </div>
      <div
        v-if="tableDataById(el.id)?.length && isDateCorrect"
        class="sensorsMain-container-alarmsTable"
      >
        <div class="sensorsMain-container-alarmsTable__title">
          <p>{{ $t('deviationsInTimeFrame') }}</p>
        </div>
        <div>
          <Table :data="tableDataById(el.id)" :columns="columnsById" />
        </div>
      </div>
    </div>
  </div>
  <div
    v-if="selectedOption?.[0]?.value === 'all' && isDateCorrect"
    class="sensorsMain"
  >
    <div class="sensorsMain-diagram">
      <LineChart
        :id="'lineChart'"
        :key="lineChartKey"
        :data-line-chart="dataLineChart"
        :options-line-chart="optionsLineChart"
        :style="styleLineChart"
      />
    </div>
    <div
      v-if="alarmsSensor.length && isDateCorrect"
      class="sensorsMain-container-alarmsTable"
    >
      <div class="sensorsMain-container-alarmsTable__title">
        <p>{{ $t('deviationsInTimeFrame') }}</p>
      </div>
      <div>
        <Table :data="alarmsSensor" :columns="columns" />
      </div>
    </div>
  </div>
  <EditSensor
    :edit-sensor-data="editSensorData"
    :pharmacy-id="pharmacy?.id"
    :show-modal-edit-sensor="showModalEditSensor"
    @close-modal="showModalEditSensor = false"
  />
  <AddComment
    :edit-sensor-data="editSensorData"
    :pharmacy-id="pharmacy?.id"
    :show-modal-add-comment="showModalAddComment"
    @close-modal="showModalAddComment = false"
  />
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import moment from 'moment';

import EditSensor from './EditSensor/EditSensor.vue';
import AddComment from './AddComment/AddComment.vue';

import {
  infoAboutSersor,
  optionsLineChart,
  columnsById,
  columns
} from './configs';
import {
  AlarmSensor,
  DataSensor,
  Sensor
} from '@/store/modules/pharmacy/types';

@Options({
  name: 'SensorsMain',
  props: [
    'pharmacy',
    'lineChartKey',
    'dataLineChart',
    'alarmsSensor',
    'isDateCorrect',
    'selectedOption',
    'selectedSensorsData',
    'temperatureSensorStamp'
  ],
  components: {
    EditSensor,
    AddComment
  },
  computed: {
    lineChartComputed(): DataSensor[] {
      return this.dataLineChart || [];
    }
  },
  data() {
    return {
      editSensorData: null,
      infoAboutSersor: infoAboutSersor(this.$t),
      optionsLineChart: optionsLineChart,
      loading: false,
      checkedSensor: true,
      showModalEditSensor: false,
      showModalAddComment: false,
      columns: columns(this.$t),
      columnsById: columnsById(this.$t),
      height: 400,
      width: 900
    };
  },
  methods: {
    chartDataById(id: string) {
      if (!id || !this.lineChartComputed?.length) return null;

      const found = this.lineChartComputed.find(
        (chart: DataSensor) => chart.id === id
      );

      return found || null;
    },
    tableDataById(id: string) {
      if (!id || !this.alarmsSensor?.length) return null;

      let found = this.alarmsSensor.find(
        (alarm: AlarmSensor) => alarm.id === id
      );

      found = found?.alarmsSensor?.sort((a: AlarmSensor, b: AlarmSensor) => {
        return b.alarmStartTimestamp - a.alarmStartTimestamp;
      });

      return found || null;
    },
    dateTime(value: number) {
      if (value) {
        return moment(value).format('DD.MM.YYYY HH:mm');
      }
    },
    initEditSensor(id: string) {
      this.editSensorData = this.pharmacy?.sensors.find(
        (sensor: Sensor) => sensor.id === id
      );

      this.showModalEditSensor = true;
    },
    initComment(id: string) {
      this.editSensorData = this.pharmacy?.sensors.find(
        (sensor: Sensor) => sensor.id === id
      );

      this.showModalAddComment = true;
    },
    styleLineChart() {
      return {
        height: `${this.height}px`,
        width: `${this.width}px`,
        position: 'relative'
      };
    }
  }
})
export default class SensorsMain extends Vue {}
</script>

<style lang="scss" scoped>
.sensorsMain {
  .sensorsMain-container {
    display: flex;
    flex-direction: column;
    margin-bottom: 32px;
    .sensorsMain-container-general {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      .sensorsMain-sensor {
        max-width: 175px;
        width: 100%;
        padding: 12px 32px;
        margin-right: 24px;
        border-radius: 8px;
        border: 1px solid $border;
        background-color: $light-grey;
        .sensorsMain-sensor-info {
          margin-bottom: 24px;
          background-color: $light-grey;
          &:last-child {
            margin-bottom: 0;
          }
          .title {
            color: $label;
            font-size: $twelveSize;
            line-height: 14px;
            margin-bottom: 7px;
            background-color: $light-grey;
          }
          .value {
            width: max-content;
            color: $mid-grey;
            font-size: $fourteenSize;
            background-color: $light-grey;
          }
        }
      }
      .sensorsMain-diagram {
        width: 900px;
        height: 400px;
        margin: 0 auto;
      }
      .sensorsMain-sensorInfo {
        display: flex;
        flex-direction: column;
        margin-left: 24px;
        .checkedSensor {
          display: flex;
          align-items: center;
          margin-bottom: 24px;
          .container {
            margin-left: 12px;
            .value {
              width: max-content;
              color: $mid-grey;
              font-size: $fourteenSize;
              margin-bottom: 7px;
            }
            .title {
              color: $label;
              font-size: $twelveSize;
              line-height: 14px;
            }
          }
        }
        .editSensor-button {
          margin-bottom: 24px;
        }
      }
    }
  }
  .sensorsMain-diagram {
    max-width: 1140px;
    width: 100%;
    height: 400px;
    margin: 0 auto;
  }
  .sensorsMain-container-alarmsTable {
    max-height: 450px;
    height: 100%;
    .sensorsMain-container-alarmsTable__title {
      margin-top: 50px;
      margin-bottom: 24px;
      p {
        font-family: 'Ubuntu-Medium';
        font-size: $sixteenSize;
        line-height: $fourteenSize;
        font-weight: 500;
      }
    }
  }
}
</style>
